import type { LoyaltyProgramSummary } from "@/types/loyalty-program"
import { LocationSearchHit } from "@/types/place"

import { Timestamp } from "./date"
import { PlaceRef } from "./entity"
import { Location } from "./place"
import type { TripUpdate } from "./trip"

export type UserRef = `u-${string}`

export function isUserRef(value: unknown): value is UserRef {
  return typeof value === "string" && value.startsWith("u-")
}

export const ExternalAuthProviders = ["google", "apple"] as const
export type ExternalAuthProvider = (typeof ExternalAuthProviders)[number]
export const LoginAuthProviders = [...ExternalAuthProviders, "email"] as const
export type LoginAuthProvider = (typeof LoginAuthProviders)[number]
// The providers remembered as "last used" on the sign-in form. Phone is not a
// LoginAuthProvider, since the email flow never offers it.
export const LastUsedProviders = [...LoginAuthProviders, "phone"] as const
export type LastUsedProvider = (typeof LastUsedProviders)[number]
export const AuthProviders = [
  ...LoginAuthProviders,
  "phone",
  "anonymous",
] as const
export type AuthProvider = (typeof AuthProviders)[number]

export interface CurrentUser {
  user_ref: UserRef
  email?: string
  first_name?: string
  last_name?: string
  is_mindtrip: boolean
  is_crawler: boolean
  provider: AuthProvider | ""
  time_zone: string
  image_url?: string
  handle?: string
  has_persona: boolean
  searchable?: boolean
  created_at: Timestamp
  email_validated: boolean
  account_verified?: boolean
  uuid: string
  functional_cookies_enabled?: boolean
  advertising_cookies_enabled?: boolean
  has_thatch_data: boolean
  has_any_trip?: boolean
  has_chatted?: boolean
  has_saved_places?: boolean
  is_from_thatch: boolean
  is_thatch_creator: boolean
  ipcountry?: string
  language?: string
  region?: string
}

export interface PublicUser {
  user_ref: UserRef
  first_name?: string
  last_name?: string
  image_url?: string
  user_profile?: PublicUserProfile
  handle: string
  searchable?: boolean
  anonymous?: boolean
}

export type Languages = {
  name: string
  level: string
}

export interface UserProfileAudience {
  platform?: string
  other?: string
  handle?: string
  follower?: string
}

export interface CorePreferences {
  experience_level?: string
  planning_style?: string
  pace_preference?: string
  variety_seeking?: number
  sensory_quadrant?: string
  downtime_needs?: string
  social_comfort?: string
  profile_confidence?: string
  needs_refresh?: boolean
  last_updated?: string
}

export interface IndividualPreference {
  id: string
  name: string
  category?: string
  importance: number
  confidence: number
  evidence: string
  persona?: string // e.g., "couple", "solo", "family", "business"
  created_at: string
  updated_at?: string
}

// Places structure for user profile (Mindtrip users only)
export interface VisitedPlace {
  id: string
  name: string
  geo_id?: string
  added_at: string
}

export interface FavoritePlace {
  id: string
  name: string
  geo_id?: string
  reason?: string
  added_at: string
}

export interface ImportantPlace {
  id: string
  name: string
  address?: string
  visible_in_chat: boolean
  added_at: string
}

export interface UserPlaces {
  visited?: VisitedPlace[]
  favorites?: FavoritePlace[]
  important?: ImportantPlace[]
}

export type ThemePreference = "light" | "dark" | "system"
export type MeasurementSystem = "us" | "metric" | "uk"

export interface SetupTripDraft {
  text: string
  added_future_location_refs?: PlaceRef[]
  trip: Pick<
    TripUpdate,
    "major_stops" | "time_frame" | "travelers" | "roadtrip"
  >
}

export interface CurrentUserProfile {
  loyalty_programs?: LoyaltyProgramSummary[]
  unresolved_setup_loyalty_programs?: string[]
  unlisted_loyalty_programs?: Omit<LoyaltyProgramSummary, "key">[]
  loyalty_programs_answered?: boolean
  setup_trip?: SetupTripDraft
  website?: string
  location?: PlaceRef
  favorite_location_refs?: PlaceRef[]
  visited_country_location_refs?: PlaceRef[]
  future_location_refs?: PlaceRef[]
  bio?: string

  street_address1?: string
  street_address2?: string
  city?: string
  state?: string
  zip?: string
  cell_phone?: string
  home_phone?: string
  locale?: string
  marketing_email_opt_out?: boolean

  home_airport_iata?: string
  seat_preference?: string
  special_assistance?: string
  flight_preference?: string
  accommodation_preference?: string
  known_traveler_number?: string
  redress_number?: string
  passport_country?: string
  passport_number?: string
  passport_expire_year?: number
  passport_expire_month?: number
  passport_expire_day?: number

  birth_day?: number
  birth_month?: number
  birth_year?: number

  about_you?: string
  your_persona?: string
  your_persona_description?: string
  your_interests?: string
  instagram?: string
  tiktok?: string
  youtube?: string
  shop_link?: string
  languages?: Languages[]
  memory_enabled?: boolean
  /** Memory knows too little to act on: show the setup invitation, and ask for setup before agents. */
  has_shallow_memory?: boolean
  payment_method?: "paypal" | "venmo"
  payment_username?: string
  payment_email?: string
  payment_phone?: string

  audiences?: UserProfileAudience[]
  followers_count?: number
  accepted_followers_count?: number
  following_count?: number
  functional_cookies_enabled?: boolean
  advertising_cookies_enabled?: boolean
  has_set_up_travel_assistant?: boolean

  bot_tone?: string
  assistant_voice?: AssistantVoicePreference
  currency_code?: string
  theme?: ThemePreference
  measurement_system?: MeasurementSystem
  language?: string
  region?: string

  // Travel personality preferences (Mindtrip users only)
  core_preferences?: CorePreferences
  preferences?: IndividualPreference[]
  places?: UserPlaces

  // Privacy settings for LLM visibility
  privacy?: PrivacySettings
}

export interface PrivacySettings {
  share_name?: boolean
  share_birthday?: boolean
}

export interface PublicUserProfile {
  website?: string
  location?: PlaceRef
  bio?: string
  your_persona?: string
  your_interests?: string
  instagram?: string
  tiktok?: string
  youtube?: string
  shop_link?: string
  languages?: Languages[]
  payment_username?: string
  payment_method?: "paypal" | "venmo"
  followers_count?: number
  accepted_followers_count?: number
  following_count?: number
}

export type CreateAnonymousRes = {
  apiServerToken: string
}

export const UserPermissions = [
  "can_publish_inspiration",
  "can_admin_inspiration",
  "can_share_trip",
  "can_join_as_creator",
  "can_publish_vanity_link",
  "can_view_vanity_reports",
  "can_view_all_vanity_reports",
  "can_admin_vanity_link",
  "can_access_dashboard",
  "can_manage_organizations",
] as const

export type UserPermission = (typeof UserPermissions)[number]

// permissions for a specific organization
export const PerOrganizationPermissions = [
  "can_edit_organization",
  "can_view_organization",
] as const

export type PerOrganizationPermission =
  (typeof PerOrganizationPermissions)[number]

export const PerInspirationPermission = ["can_read_inspiration"] as const

export type PerInspirationPermission = (typeof PerInspirationPermission)[number]

export type UserLocation = Location | LocationSearchHit

export type FollowStatus =
  | "notfollowing"
  | "requested"
  | "accepted"
  | "denied"
  | "automatic"
  | undefined

export interface FollowerInfo {
  user: PublicUser
  status: FollowStatus // does the current user follow this user?
}

export interface UserProfileUpdates {
  setup_trip?: SetupTripDraft | null
  first_name?: string
  last_name?: string
  handle?: string
  website?: string
  location?: string
  favorite_location_refs?: string[]
  visited_country_location_refs?: string[]
  future_location_refs?: string[]
  bio?: string
  shop_link?: string
  instagram?: string
  tiktok?: string
  youtube?: string
  email?: string
  street_address1?: string
  street_address2?: string
  city?: string
  state?: string
  zip?: string
  cell_phone?: string
  home_phone?: string
  time_zone?: string
  locale?: string

  marketing_email_opt_out?: boolean
  home_airport_iata?: string
  seat_preference?: string
  special_assistance?: string
  flight_preference?: string
  accommodation_preference?: string
  known_traveler_number?: string
  redress_number?: string
  passport_country?: string
  passport_number?: string
  passport_expire_year?: number
  passport_expire_month?: number
  passport_expire_day?: number

  birth_day?: number
  birth_month?: number
  birth_year?: number

  about_you?: string
  your_persona?: string
  your_persona_description?: string
  your_interests?: string
  bot_tone?: string
  currency_code?: string
  theme?: ThemePreference
  measurement_system?: MeasurementSystem
  language?: string
  region?: string
  assistant_voice?: AssistantVoicePreference
  functional_cookies_enabled?: boolean
  advertising_cookies_enabled?: boolean

  // Creator program
  payment_method?: string
  payment_username?: string
  payment_email?: string
  payment_phone?: string
  accepted_terms?: Timestamp

  audiences?: UserProfileAudience[]

  organization_email_opt_in?: boolean

  // Privacy settings for LLM visibility
  privacy?: PrivacySettings
}

export type AssistantVoicePreference =
  | {
      provider: "elevenlabs"
      voice_id: string
      speed?: number
    }
  | {
      provider: "openai"
      voice: OpenAIRealtimeVoice
    }

export type OpenAIRealtimeVoice = string

export function isOpenAIRealtimeVoice(
  value: unknown
): value is OpenAIRealtimeVoice {
  return typeof value === "string" && value.trim().length > 0
}

export interface BotToneOption {
  key: string
  display_name: string
  description: string
}

export interface DefaultBotToneOption {
  display_name: string
  description: string
}

export interface CurrencyOption {
  code: string
  name: string
  symbol: string
  preferred: boolean
}

export interface UserProfileOptions {
  default_bot_tone?: DefaultBotToneOption
  bot_tone?: BotToneOption[]
  currencies?: CurrencyOption[]
  preferred_currency?: string
}

export interface GetCurrenciesResponse {
  currencies: CurrencyOption[]
  preferred_currency?: string
}

export interface UpdateUserProfileResults {
  user: CurrentUser
  user_profile: CurrentUserProfile
}

export interface UserPasswordUpdate {
  current_password: string
  new_password: string
}

export interface UpdateUserPasswordResults {
  user: CurrentUser
}

export type UpdateUserPropertiesType = "utm-props" | "amend-magic-link"

export interface UpdateUserProperties {
  type?: UpdateUserPropertiesType
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  epik?: string
}

export interface UpdateUserPropertiesResults {
  user: CurrentUser
}
